import { log as _log } from "../../../../utils/ChatStreamLogger";

const MAX_TYPED_CHUNK_ERRORS = 20;

function nowIso() {
  return new Date().toISOString();
}

function cloneMessage(message) {
  return {
    ...message,
    parts: Array.isArray(message.parts)
      ? message.parts.map((part) => ({ ...part }))
      : [],
  };
}

function cloneState(previousState) {
  return {
    ...previousState,
    messages: Array.isArray(previousState.messages)
      ? previousState.messages.map(cloneMessage)
      : [],
    activeTextPartById: { ...(previousState.activeTextPartById || {}) },
    activeToolPartByCallId: { ...(previousState.activeToolPartByCallId || {}) },
    errors: Array.isArray(previousState.errors) ? [...previousState.errors] : [],
    lastError: null,
  };
}

function appendError(next, code, message, chunk) {
  const error = {
    code,
    message,
    chunkType: chunk?.type || "unknown",
    at: nowIso(),
  };
  next.lastError = error;
  next.errors.push(error);
  if (next.errors.length > MAX_TYPED_CHUNK_ERRORS) {
    next.errors.shift();
  }
}

function getMessageIndexById(next, messageId) {
  return next.messages.findIndex((msg) => msg.id === messageId);
}

function ensureMessage(next, messageId) {
  let messageIndex = getMessageIndexById(next, messageId);
  if (messageIndex === -1) {
    next.messages.push({
      id: messageId,
      role: "assistant",
      status: "streaming",
      parts: [],
      createdAt: nowIso(),
      updatedAt: nowIso(),
    });
    messageIndex = next.messages.length - 1;
  }
  return messageIndex;
}

function appendPart(next, messageId, part) {
  const messageIndex = ensureMessage(next, messageId);
  const message = next.messages[messageIndex];
  message.parts.push(part);
  message.updatedAt = nowIso();
  return {
    messageIndex,
    partIndex: message.parts.length - 1,
  };
}

function updatePart(next, messageId, partIndex, updater) {
  const messageIndex = getMessageIndexById(next, messageId);
  if (messageIndex < 0) return false;
  const message = next.messages[messageIndex];
  if (!Array.isArray(message.parts) || partIndex < 0 || partIndex >= message.parts.length) {
    return false;
  }
  const previousPart = message.parts[partIndex];
  message.parts[partIndex] = updater(previousPart);
  message.updatedAt = nowIso();
  return true;
}

function finalizeActiveTextParts(next) {
  const entries = Object.entries(next.activeTextPartById || {});
  for (const [textPartId, pointer] of entries) {
    const ok = updatePart(next, pointer.messageId, pointer.partIndex, (part) => ({
      ...part,
      state: "done",
    }));
    if (!ok) {
      appendError(
        next,
        "MISSING_ACTIVE_TEXT_PART",
        `could not finalize active text part ${textPartId}`,
        { type: "finish" }
      );
    }
  }
  next.activeTextPartById = {};
}

function upsertToolPartPointer(next, chunk, fallbackToolName = "unknown_tool") {
  const existingPointer = next.activeToolPartByCallId[chunk.toolCallId];
  if (existingPointer) return existingPointer;

  const messageId =
    next.currentMessageId || `assistant-${Date.now()}-${Math.max(1, next.chunkCount || 0)}`;
  const { partIndex } = appendPart(next, messageId, {
    type: "tool",
    toolCallId: chunk.toolCallId,
    toolName: chunk.toolName || fallbackToolName,
    state: "input-streaming",
    inputText: "",
    input: undefined,
    output: undefined,
    errorText: null,
  });
  const pointer = { messageId, partIndex };
  next.activeToolPartByCallId[chunk.toolCallId] = pointer;
  return pointer;
}

export function createUiChunkSessionState(chatSessionId) {
  return {
    chatSessionId: String(chatSessionId || ""),
    streamVersion: "v2",
    messages: [],
    currentMessageId: null,
    activeTextPartById: {},
    activeToolPartByCallId: {},
    chunkCount: 0,
    finished: false,
    finishReason: null,
    lastChunkAt: null,
    lastChunkType: null,
    errors: [],
    lastError: null,
  };
}

export function applyUiChunk(previousState, chunk) {
  const next = cloneState(previousState || createUiChunkSessionState(""));
  next.chunkCount = Number(next.chunkCount || 0) + 1;
  next.lastChunkAt = nowIso();
  next.lastChunkType = chunk?.type || "unknown";

  if (!chunk || typeof chunk !== "object") {
    appendError(next, "INVALID_CHUNK", "chunk must be an object", chunk);
    return next;
  }

  switch (chunk.type) {
    case "start": {
      if (next.finished) {
        next.activeTextPartById = {};
        next.activeToolPartByCallId = {};
      }
      const messageId =
        typeof chunk.messageId === "string" && chunk.messageId.trim()
          ? chunk.messageId.trim()
          : `assistant-${Date.now()}-${next.chunkCount}`;
      ensureMessage(next, messageId);
      next.currentMessageId = messageId;
      next.finished = false;
      next.finishReason = null;
      break;
    }

    case "text-start": {
      if (!next.currentMessageId) {
        appendError(
          next,
          "TEXT_START_WITHOUT_START",
          "received text-start before start",
          chunk
        );
        break;
      }
      const pointer = appendPart(next, next.currentMessageId, {
        type: "text",
        id: chunk.id,
        text: "",
        state: "streaming",
      });
      next.activeTextPartById[chunk.id] = {
        messageId: next.currentMessageId,
        partIndex: pointer.partIndex,
      };
      break;
    }

    case "text-delta": {
      const pointer = next.activeTextPartById[chunk.id];
      if (!pointer) {
        appendError(
          next,
          "TEXT_DELTA_WITHOUT_TEXT_START",
          `received text-delta for unknown text part ${chunk.id}`,
          chunk
        );
        break;
      }
      const updated = updatePart(next, pointer.messageId, pointer.partIndex, (part) => ({
        ...part,
        text: `${part.text || ""}${chunk.delta || ""}`,
      }));
      if (!updated) {
        appendError(
          next,
          "TEXT_POINTER_INVALID",
          `text part pointer invalid for ${chunk.id}`,
          chunk
        );
      }
      break;
    }

    case "text-end": {
      const pointer = next.activeTextPartById[chunk.id];
      if (!pointer) {
        appendError(
          next,
          "TEXT_END_WITHOUT_TEXT_START",
          `received text-end for unknown text part ${chunk.id}`,
          chunk
        );
        break;
      }
      const updated = updatePart(next, pointer.messageId, pointer.partIndex, (part) => ({
        ...part,
        state: "done",
      }));
      if (!updated) {
        appendError(
          next,
          "TEXT_POINTER_INVALID",
          `text part pointer invalid for ${chunk.id}`,
          chunk
        );
      }
      delete next.activeTextPartById[chunk.id];
      break;
    }

    case "tool-input-start": {
      if (!next.currentMessageId) {
        appendError(
          next,
          "TOOL_INPUT_START_WITHOUT_START",
          "received tool-input-start before start",
          chunk
        );
        break;
      }
      const existingToolPointer = next.activeToolPartByCallId[chunk.toolCallId];
      if (existingToolPointer) {
        _log("Chunks", `TOOL reuse existing callId=${chunk.toolCallId} partIdx=${existingToolPointer.partIndex}`);
        updatePart(next, existingToolPointer.messageId, existingToolPointer.partIndex, (part) => ({
          ...part,
          toolName: chunk.toolName || part.toolName,
        }));
        break;
      }
      _log("Chunks", `TOOL new callId=${chunk.toolCallId} name=${chunk.toolName}`);
      const pointer = appendPart(next, next.currentMessageId, {
        type: "tool",
        toolCallId: chunk.toolCallId,
        toolName: chunk.toolName,
        state: "input-streaming",
        inputText: "",
        input: undefined,
        output: undefined,
        errorText: null,
      });
      next.activeToolPartByCallId[chunk.toolCallId] = {
        messageId: next.currentMessageId,
        partIndex: pointer.partIndex,
      };
      break;
    }

    case "tool-input-delta": {
      const pointer = upsertToolPartPointer(next, chunk);
      const updated = updatePart(next, pointer.messageId, pointer.partIndex, (part) => ({
        ...part,
        state: "input-streaming",
        inputText: `${part.inputText || ""}${chunk.inputTextDelta || ""}`,
      }));
      if (!updated) {
        appendError(
          next,
          "TOOL_POINTER_INVALID",
          `tool pointer invalid for ${chunk.toolCallId}`,
          chunk
        );
      }
      break;
    }

    case "tool-input-available": {
      const pointer = upsertToolPartPointer(next, chunk, chunk.toolName);
      const updated = updatePart(next, pointer.messageId, pointer.partIndex, (part) => ({
        ...part,
        toolName: chunk.toolName || part.toolName,
        state: "input-available",
        input: chunk.input,
      }));
      if (!updated) {
        appendError(
          next,
          "TOOL_POINTER_INVALID",
          `tool pointer invalid for ${chunk.toolCallId}`,
          chunk
        );
      }
      break;
    }

    case "tool-output-available": {
      _log("Chunks", `TOOL output callId=${chunk.toolCallId}`);
      const pointer = upsertToolPartPointer(next, chunk);
      const updated = updatePart(next, pointer.messageId, pointer.partIndex, (part) => ({
        ...part,
        state: "output-available",
        output: chunk.output,
        errorText: null,
      }));
      if (!updated) {
        appendError(
          next,
          "TOOL_POINTER_INVALID",
          `tool pointer invalid for ${chunk.toolCallId}`,
          chunk
        );
      }
      break;
    }

    case "tool-output-error": {
      const pointer = upsertToolPartPointer(next, chunk);
      const updated = updatePart(next, pointer.messageId, pointer.partIndex, (part) => ({
        ...part,
        state: "output-error",
        errorText: chunk.errorText,
      }));
      if (!updated) {
        appendError(
          next,
          "TOOL_POINTER_INVALID",
          `tool pointer invalid for ${chunk.toolCallId}`,
          chunk
        );
      }
      break;
    }

    case "finish": {
      const toolParts = next.messages.flatMap(m => (m.parts || []).filter(p => p.type === "tool"));
      if (toolParts.length > 0) {
        _log("Chunks", `FINISH toolParts=${toolParts.length}: ${toolParts.map(p => `${p.toolCallId}(${p.state})`).join(", ")}`);
      }
      finalizeActiveTextParts(next);
      for (const [, pointer] of Object.entries(next.activeToolPartByCallId || {})) {
        updatePart(next, pointer.messageId, pointer.partIndex, (part) => {
          if (part.state === "input-streaming" || part.state === "input-available") {
            return { ...part, state: part.input !== undefined ? "input-available" : "done" };
          }
          return part;
        });
      }
      for (let i = 0; i < next.messages.length; i++) {
        if (next.messages[i].status === "streaming") {
          next.messages[i] = {
            ...next.messages[i],
            status: "done",
            updatedAt: nowIso(),
          };
        }
      }
      next.currentMessageId = null;
      next.activeToolPartByCallId = {};
      next.finished = true;
      next.finishReason =
        typeof chunk.finishReason === "string" && chunk.finishReason.trim()
          ? chunk.finishReason.trim()
          : "stop";
      break;
    }

    default:
      appendError(next, "UNKNOWN_CHUNK_TYPE", `unknown chunk type ${chunk.type}`, chunk);
      break;
  }

  return next;
}

export function getUiChunkDebugSummary(state) {
  if (!state) {
    return {
      chunkCount: 0,
      messageCount: 0,
      errorCount: 0,
      lastChunkType: null,
      finished: false,
      finishReason: null,
    };
  }

  return {
    chunkCount: Number(state.chunkCount || 0),
    messageCount: Array.isArray(state.messages) ? state.messages.length : 0,
    errorCount: Array.isArray(state.errors) ? state.errors.length : 0,
    lastChunkType: state.lastChunkType || null,
    finished: Boolean(state.finished),
    finishReason: state.finishReason || null,
  };
}
